import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';

import { EquipmentType } from '@interfaces';
import { EquipmentsTypesApi } from 'app/main/settings/registers/equipments-types/services/api/equipments-types-api.service';

const INPUT_FIELD_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => EquipmentsTypeComponent),
  multi: true
};

@Component({
  selector: 'equipments-type',
  templateUrl: './equipments-type.component.html',
  styleUrls: ['./equipments-type.component.scss'],
  providers: [INPUT_FIELD_VALUE_ACCESSOR]
})
export class EquipmentsTypeComponent implements ControlValueAccessor, OnInit {
  @Input() required: boolean;
  @Input() floatLabel: boolean;
  @Input() disabled = false;
  @Input() appearance = 'outline';

  public equipmentsType: EquipmentType[] = [];
  public inputValue = new FormControl();
  public selectedEquipmentType: EquipmentType | null;
  public loading = false;

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private equipmentsTypesApi: EquipmentsTypesApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    if (this.required) {
      this.inputValue.setValidators(Validators.required);
    }

    if (this.disabled) {
      this.inputValue.disable();
    }

    this.inputValue.valueChanges
      .pipe(debounceTime(500))
      .subscribe((searchTerm: string | null) => {
        this.equipmentsType = [];
        if (searchTerm) {
          this.searchEquipmentsTypes(searchTerm);
        }
    });
  }

  onChange: (_: any) => void = () => { };
  onTouched: (_: any) => void = () => { };
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  searchEquipmentsTypes(searchTerm: string): void {
    this.loading = true;

    const options = {
      where: {
        name_contains: searchTerm,
        archived: false
      },
      orderBy: 'name_asc',
      limit: 10,
      offset: 0
    };

    this.equipmentsTypesApi
      .listEquipmentsTypes(options)
      .subscribe((result) => {
        this.equipmentsType = result.items;
        this.loading = false;
      });
  }

  writeValue(equipmentType: EquipmentType): void {
    if (!equipmentType || !equipmentType.id) {
      return this.deselectEquipmentType();
    }

    this.equipmentsTypesApi
      .getEquipmentTypeById(equipmentType.id)
      .subscribe((result) => {
        this.selectedEquipmentType = result;
        this.inputValue.setErrors(null);
      });
  }

  setSelectedEquipmentType(): void {
    if (this.inputValue.value) {
      this.selectedEquipmentType = this.equipmentsType.find((equipmentType) => {
        return equipmentType.id === this.inputValue.value;
      });

      this.onChange(this.selectedEquipmentType);
      this.inputValue.setErrors(null);
    }
  }

  deselectEquipmentType(): void {
    this.onChange(null);
    this.selectedEquipmentType = null;
    this.inputValue.setValue(null);
    this.inputValue.setErrors(null);
  }
}
